import React, { useMemo, useState } from "react";
import { Archive as ArchiveIcon } from "lucide-react";
import PipelineKanban from "./PipelineKanban";
import PipelineCard from "./PipelineCard";
import EmptyState from "../../common/EmptyState";

/**
 * Pipeline — top-level mini-CRM container.
 *
 * Renders the new pipeline UI behind shouldShowMiniCRM(). For Session 1:
 *   - Search box (filters by customer name + scope_summary)
 *   - Archive toggle (show / hide archived + Declined leads)
 *   - Kanban view of the 5 active stages (PipelineKanban)
 *   - Archive view: simple flat list of Declined + archived leads with
 *     filter chips (All / Declined / Completed)
 *   - No drag-drop yet — moves via the dropdown inside each PipelineCard.
 *
 * Wires straight into TradieDashboard. Replaces the existing
 * <LeadMarketplace> + <LeadDetailPanel> two-pane layout for the V1 quote
 * pipeline. (LeadMarketplace stays alive for the V2 marketplace flow.)
 */
export default function Pipeline({
  requests,
  updateLeadStage,
  archiveLead,
  unarchiveLead,
  updateLeadContactDetails,
  onOpenDetail,
}) {
  const [query, setQuery] = useState("");
  const [showArchive, setShowArchive] = useState(false);
  const [archiveFilter, setArchiveFilter] = useState("all"); // all | declined | completed

  const { activeLeads, archivedLeads } = useMemo(() => {
    const active = [];
    const archived = [];
    for (const lead of requests || []) {
      const isDeclined = lead.status === "Declined";
      const isArchived = !!lead.archived_at;
      if (isDeclined || isArchived) archived.push(lead);
      else active.push(lead);
    }
    return { activeLeads: active, archivedLeads: archived };
  }, [requests]);

  const filteredActive = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeLeads;
    return activeLeads.filter((l) => {
      const hay = [
        l.customer,
        l.form?.fullName,
        l.scope_summary,
        l.trade,
        l.suburb,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [activeLeads, query]);

  const filteredArchived = useMemo(() => {
    if (archiveFilter === "declined") {
      return archivedLeads.filter((l) => l.status === "Declined");
    }
    if (archiveFilter === "completed") {
      return archivedLeads.filter(
        (l) => l.status !== "Declined" && l.archived_at
      );
    }
    return archivedLeads;
  }, [archivedLeads, archiveFilter]);

  return (
    <div data-tab-content>
      {/* Toolbar — search + archive toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "14px",
          flexWrap: "wrap",
        }}
      >
        <div
          className="rq-input"
          style={{ flex: "0 1 320px", maxWidth: "320px", minWidth: 0 }}
        >
          <input
            type="text"
            placeholder="Search leads…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={() => setShowArchive((s) => !s)}
          className={`btn ${showArchive ? "btn-primary" : "btn-secondary"}`}
          style={{ padding: "6px 12px", fontSize: "0.82rem" }}
        >
          <ArchiveIcon size={14} />{" "}
          {showArchive
            ? `Hide archive`
            : `Show archive${archivedLeads.length ? ` (${archivedLeads.length})` : ""}`}
        </button>
      </div>

      {/* Active board OR archive list */}
      {!showArchive ? (
        filteredActive.length === 0 && activeLeads.length === 0 ? (
          <EmptyState
            icon="✨"
            title="No leads in your pipeline yet"
            description="Share your direct intake link with customers, or new requests from existing flows will land here ready to quote."
          />
        ) : (
          <PipelineKanban
            leads={filteredActive}
            onUpdateStage={updateLeadStage}
            onArchive={archiveLead}
            onOpenDetail={onOpenDetail}
            onUpdateContactDetails={updateLeadContactDetails}
            onMarkPaid={(id) => updateLeadStage(id, "Done")}
          />
        )
      ) : (
        <ArchiveView
          leads={filteredArchived}
          totalArchived={archivedLeads.length}
          filter={archiveFilter}
          onFilterChange={setArchiveFilter}
          onUpdateStage={updateLeadStage}
          onUnarchive={unarchiveLead}
          onOpenDetail={onOpenDetail}
          onUpdateContactDetails={updateLeadContactDetails}
        />
      )}
    </div>
  );
}

// ── Archive view (simple list with filter chips) ───────────────────────────
function ArchiveView({
  leads,
  totalArchived,
  filter,
  onFilterChange,
  onUpdateStage,
  onUnarchive,
  onOpenDetail,
  onUpdateContactDetails,
}) {
  const chips = [
    { id: "all", label: `All (${totalArchived})` },
    { id: "declined", label: "Declined" },
    { id: "completed", label: "Completed" },
  ];

  return (
    <div>
      {/* Filter chips */}
      <div
        style={{
          display: "flex",
          gap: "6px",
          marginBottom: "12px",
          flexWrap: "wrap",
        }}
      >
        {chips.map((c) => {
          const active = filter === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onFilterChange(c.id)}
              style={{
                background: active ? "var(--primary)" : "transparent",
                color: active ? "#fff" : "var(--text-soft, #6b5448)",
                border: active
                  ? "1px solid var(--primary)"
                  : "1px solid var(--border, rgba(140,100,80,0.18))",
                borderRadius: "999px",
                padding: "5px 12px",
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {leads.length === 0 ? (
        <EmptyState
          icon="📦"
          title="Nothing archived"
          description="Declined leads and completed jobs land here when archived. Stays out of your active pipeline but still searchable."
        />
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            maxWidth: "640px",
          }}
        >
          {leads.map((lead) => (
            <PipelineCard
              key={lead.id}
              lead={lead}
              onUpdateStage={onUpdateStage}
              onArchive={onUnarchive}
              onOpenDetail={onOpenDetail}
              onUpdateContactDetails={onUpdateContactDetails}
            />
          ))}
        </div>
      )}
    </div>
  );
}
